import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PlayerGameData } from "../types";
import type { PlayerLobbyData } from "../../../store";
import type { ThemeColors } from "../../../theme";
import { ProviderBadge } from "../../../../components/ProviderBadge";
import { ProfileAvatar } from "../../../../components/ProfileAvatar";
import {
  getOpenLobbyPlayerName,
  sortOpenLobbyPlayerEntries,
} from "../playerSorting";

const REVEAL_DELAY_MS = 20_000;

interface OpenLobbyPlayerListProps {
  gamePlayers: Record<string, PlayerGameData>;
  lobbyPlayers: Record<string, PlayerLobbyData> | undefined;
  currentPlayerId: string;
  startTime: string;
  theme: ThemeColors;
  isDark: boolean;
}

export function OpenLobbyPlayerList({
  gamePlayers,
  lobbyPlayers,
  currentPlayerId,
  startTime,
  theme,
  isDark,
}: OpenLobbyPlayerListProps) {
  const { t, i18n } = useTranslation();

  const [articlesRevealed, setArticlesRevealed] = useState(
    () => Date.now() - new Date(startTime).getTime() >= REVEAL_DELAY_MS,
  );

  useEffect(() => {
    if (articlesRevealed) return;
    const elapsed = Date.now() - new Date(startTime).getTime();
    const remaining = REVEAL_DELAY_MS - elapsed;
    const id = setTimeout(
      () => setArticlesRevealed(true),
      Math.max(0, remaining),
    );
    return () => clearTimeout(id);
  }, [startTime, articlesRevealed]);

  const entries = useMemo(
    () =>
      sortOpenLobbyPlayerEntries(
        gamePlayers,
        lobbyPlayers,
        i18n.resolvedLanguage ?? i18n.language,
      ),
    [gamePlayers, i18n.language, i18n.resolvedLanguage, lobbyPlayers],
  );

  return (
    <div
      data-testid="open-lobby-player-list"
      className={`${theme.sectionBg} ${theme.sectionBorder} sm:rounded-xl sm:p-4 w-full h-full flex flex-col relative`}
    >
      <div
        className={`flex items-center gap-2 mb-4 pb-3 border-b ${isDark ? "border-zinc-800/50" : "border-gray-200"}`}
      >
        <svg
          className={`w-4 h-4 ${isDark ? "text-zinc-400" : "text-gray-500"}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <span
          className={`text-xs font-semibold uppercase tracking-wider ${theme.textSecondary}`}
        >
          {t("components.playerList.playersWithCount", {
            count: entries.length,
          })}
        </span>
      </div>

      <div className="flex-1 space-y-1 overflow-auto">
        {entries.map(([playerId, data]) => {
          const playerName = getOpenLobbyPlayerName(playerId, lobbyPlayers);
          const isCurrentPlayer = playerId === currentPlayerId;
          {
            /* const clicks = data.past_articles.length; */
          }
          const lastArticle =
            data.past_articles.length > 0
              ? data.past_articles[data.past_articles.length - 1]?.article.title
              : null;

          return (
            <div
              key={playerId}
              className={`flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                isCurrentPlayer
                  ? isDark
                    ? "bg-zinc-800/50 border border-zinc-700/50"
                    : "bg-gray-50 border border-gray-200/50"
                  : isDark
                    ? "hover:bg-zinc-900/50"
                    : "hover:bg-gray-100"
              }`}
            >
              <div
                className="flex min-w-0 flex-1 items-center gap-2"
                data-testid="player-identity"
              >
                <ProfileAvatar
                  image={lobbyPlayers?.[playerId]?.image}
                  name={playerName}
                  size="sm"
                />
                <div className="flex min-w-0 flex-1 flex-col items-start">
                  <span
                    className={`block max-w-full truncate text-sm ${isCurrentPlayer ? "font-medium" : ""} ${theme.text}`}
                    data-testid="player-name"
                  >
                    {playerName?.slice(0, 15)}
                  </span>
                  {((lobbyPlayers?.[playerId]?.provider !== "username" &&
                    Boolean(lobbyPlayers?.[playerId]?.provider)) ||
                    isCurrentPlayer) && (
                    <div className="mt-1 flex items-center gap-1.5">
                      <ProviderBadge
                        provider={
                          lobbyPlayers?.[playerId]?.provider === "username"
                            ? null
                            : lobbyPlayers?.[playerId]?.provider
                        }
                      />
                      {isCurrentPlayer && (
                        <span
                          className={`rounded-md border px-1.5 py-0.5 text-[10px] leading-none font-medium ${theme.badge}`}
                        >
                          {t("components.playerList.you")}
                        </span>
                      )}
                    </div>
                  )}
                  {lastArticle && (
                    <span
                      className={`mt-1 block max-w-full truncate text-xs ${theme.textMuted} ${articlesRevealed ? "" : "blur-sm select-none"}`}
                      title={articlesRevealed ? lastArticle : ""}
                    >
                      {`@${lastArticle}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
